import { Box, BoxProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { uppercaseFirstLetter } from "app/helpers/string";
import { stylesMode } from "app/theme/data";
import { createColorWithAlphaChannel } from "app/theme/helpers/createColorWithAlphaChannel";
import { multiplySize } from "app/theme/helpers/multiplySize";
import { forwardRef } from "react";

const labelClasses = {
  icon: "mnl__label__icon",
  root: "mnl__label__root",
};

export type LabelProps = BoxProps & {
  color?: LabelColor;
  endIcon?: null | React.ReactElement;
  startIcon?: null | React.ReactElement;
  variant?: LabelVariant;
};

type LabelColor =
  | "default"
  | "error"
  | "info"
  | "primary"
  | "secondary"
  | "success"
  | "warning";

type LabelVariant = "filled" | "inverted" | "outlined" | "soft";

export const Label = forwardRef<HTMLSpanElement, LabelProps>(function Label(
  {
    children,
    className,
    color = "default",
    endIcon,
    startIcon,
    sx,
    variant = "soft",
    ...other
  },
  ref,
) {
  return (
    <StyledLabelBox
      className={labelClasses.root.concat(className ? ` ${className}` : "")}
      ownerState={{ color, variant }}
      ref={ref}
      sx={{
        ...(startIcon && { pl: 0.75 }),
        ...(endIcon && { pr: 0.75 }),
        ...sx,
      }}
      {...other}
    >
      {startIcon && <LabelIcon icon={startIcon} mr={0.75} />}

      {typeof children === "string" ? uppercaseFirstLetter(children) : children}

      {endIcon && <LabelIcon icon={endIcon} ml={0.75} />}
    </StyledLabelBox>
  );
});

const LabelBox = forwardRef(function LabelBox(props, ref) {
  return <Box component="span" ref={ref} {...props} />;
}) as typeof Box;

type StyledLabelBoxExtraProps = {
  ownerState: {
    color: LabelColor;
    variant: LabelVariant;
  };
};

const StyledLabelBox = styled(LabelBox, {
  shouldForwardProp: (prop) => prop !== "ownerState",
})<StyledLabelBoxExtraProps>(({ ownerState: { color, variant }, theme }) => {
  const defaultColor = {
    ...(color === "default" && {
      ...(variant === "filled" && {
        backgroundColor: theme.vars.palette.text.primary,
        color: theme.vars.palette.common.white,
        [stylesMode.dark]: { color: theme.vars.palette.grey[800] },
      }),
      ...(variant === "outlined" && {
        backgroundColor: "transparent",
        border: `2px solid ${theme.vars.palette.text.primary}`,
        color: theme.vars.palette.text.primary,
      }),
      ...(variant === "soft" && {
        backgroundColor: createColorWithAlphaChannel(
          theme.vars.palette.grey["500Channel"],
          0.16,
        ),
        color: theme.vars.palette.text.secondary,
      }),
      ...(variant === "inverted" && {
        backgroundColor: theme.vars.palette.grey[300],
        color: theme.vars.palette.grey[800],
      }),
    }),
  };

  const styleColors = {
    ...(color !== "default" && {
      ...(variant === "filled" && {
        backgroundColor: theme.vars.palette[color].main,
        color: theme.vars.palette[color].contrastText,
      }),
      ...(variant === "outlined" && {
        backgroundColor: "transparent",
        border: `2px solid ${theme.vars.palette[color].main}`,
        color: theme.vars.palette[color].main,
      }),
      ...(variant === "soft" && {
        backgroundColor: createColorWithAlphaChannel(
          theme.vars.palette[color].mainChannel,
          0.16,
        ),
        color: theme.vars.palette[color].dark,
        [stylesMode.dark]: { color: theme.vars.palette[color].light },
      }),
      ...(variant === "inverted" && {
        backgroundColor: theme.vars.palette[color].lighter,
        color: theme.vars.palette[color].darker,
      }),
    }),
  };

  return {
    alignItems: "center",
    borderRadius: multiplySize(theme.shape.borderRadius, 0.75),
    cursor: "default",
    display: "inline-flex",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    height: 24,
    justifyContent: "center",
    lineHeight: 0,
    minWidth: 24,
    padding: theme.spacing(0, 0.75),
    transition: theme.transitions.create("all", {
      duration: theme.transitions.duration.shorter,
    }),
    whiteSpace: "nowrap",
    ...defaultColor,
    ...styleColors,
  };
});

const iconStyles = {
  "& svg, img": {
    height: 1,
    objectFit: "cover",
    width: 1,
  },
  height: 16,
  width: 16,
};

type LabelIconProps = {
  icon: React.ReactElement;
  ml?: number;
  mr?: number;
};

const LabelIcon = ({ icon, ml = 0, mr = 0 }: LabelIconProps) => {
  return (
    <Box
      className={labelClasses.icon}
      component="span"
      sx={{ ml, mr, ...iconStyles }}
    >
      {icon}
    </Box>
  );
};
